import { useState } from "react";
import { toast } from "sonner";
import { Ruler, ShoppingBag, FileText, Sparkles, Plus, Minus } from "lucide-react";
import { BOX, PARKING, SURFACES_M2, SURFACES_DEVIS } from "@/data/surfaceServices";
import { useCart } from "@/contexts/CartContext";
import { usePricing } from "@/contexts/PricingContext";
import { useContent } from "@/contexts/ContentContext";

export default function Surfaces() {
  const { addItem } = useCart();
  const { content } = useContent();
  const custom = content.custom?.surfaces || [];
  const customM2 = custom.filter((c) => c.kind === "m2").map((c) => ({
    id: c.id, name: c.name, desc: c.desc, pricePerM2: Number(c.price) || 0, image: c.image,
  }));
  const customDevis = custom.filter((c) => c.kind === "devis").map((c) => ({
    id: c.id, name: c.name, desc: c.desc, image: c.image,
  }));

  const addDevis = (service) => {
    addItem({
      id: `surface_devis_${service.id}`,
      type: "surface",
      label: service.name,
      price: 0,
      quantity: 1,
      meta: "Sur devis — précisez la surface via WhatsApp",
    });
    toast.success(`${service.name} ajouté au panier (sur devis)`);
  };

  return (
    <section id="surfaces" className="relative py-20 lg:py-28 overflow-hidden" data-testid="surfaces-section">
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-[#E50914]/8 rounded-full blur-[160px] pointer-events-none"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Pitch */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-14">
          <Pitch icon={Sparkles} title="Haute pression" desc="Matériel pro, dégraissage et démoussage en profondeur" />
          <Pitch icon={Ruler} title="Tarif clair" desc="Box, parking et petites surfaces à prix fixe ou au m²" />
          <Pitch icon={FileText} title="Devis gratuit" desc="Grandes surfaces et locaux pros : devis sur-mesure" />
        </div>

        {/* Box & parking */}
        <div className="mb-16">
          <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">/ 01 — Box & stationnement</div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-2">Box & places de parking</h2>
          <p className="text-white/50 text-sm mb-8">Choisissez votre option ou le nombre de places, puis ajoutez au panier.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <BoxCard addItem={addItem} />
            <ParkingCard addItem={addItem} />
          </div>
        </div>

        {/* Petites surfaces — au m² */}
        <div className="mb-16">
          <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">/ 02 — Petites surfaces (au m²)</div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-2">Balcon, terrasse, cour…</h2>
          <p className="text-white/50 text-sm mb-8">Indiquez votre surface en m², le prix se calcule automatiquement.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...SURFACES_M2, ...customM2].map((s) => <SurfaceM2Card key={s.id} service={s} addItem={addItem} />)}
          </div>
        </div>

        {/* Grandes surfaces & pros — sur devis */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">/ 03 — Grandes surfaces & professionnels</div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-2">Locaux commerciaux & grandes surfaces</h2>
          <p className="text-white/50 text-sm mb-8">Tarif sur devis selon la surface et l'état. Ajoutez au panier, on confirme par WhatsApp.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[...SURFACES_DEVIS, ...customDevis].map((s) => {
              const Icon = s.icon || FileText;
              return (
                <div key={s.id} data-testid={`surface-devis-${s.id}`}
                  className="group p-6 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl hover:border-[#E50914]/40 hover:-translate-y-1 transition-all flex flex-col">
                  {s.image ? (
                    <div className="relative h-28 -mx-6 -mt-6 mb-5 overflow-hidden">
                      <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.style.display='none';}} />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#E50914]/20 to-transparent border border-[#E50914]/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                      <Icon className="w-5 h-5 text-[#E50914]" strokeWidth={2} />
                    </div>
                  )}
                  <h3 className="font-bold text-white text-lg mb-1">{s.name}</h3>
                  <p className="text-xs text-white/50 mb-5 leading-relaxed flex-1">{s.desc}</p>
                  <div className="pt-4 border-t border-white/10 mb-4">
                    <div className="text-[10px] uppercase tracking-wider text-white/40">Tarif</div>
                    <div className="text-sm font-bold text-white">Sur devis</div>
                  </div>
                  <button type="button" onClick={() => addDevis(s)} data-testid={`surface-adddevis-${s.id}`}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-xs tracking-wider px-4 py-3 rounded-lg border border-[#E50914] hover:shadow-[0_0_20px_rgba(229,9,20,0.4)] transition-all">
                    <ShoppingBag className="w-4 h-4" /> Ajouter au panier
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <p className="mt-10 text-center text-sm text-white/40">
          Ajoutez vos prestations au panier, puis envoyez votre demande via WhatsApp.
        </p>
      </div>
    </section>
  );
}

/* --- Box / Garage : 2 options (1 place / 2 places) --- */
function BoxCard({ addItem }) {
  const [opt, setOpt] = useState(BOX.options[0]);
  const Icon = BOX.icon;
  const { pricing } = usePricing();
  const price = pricing.surfaces?.["box_" + opt.id] ?? opt.price;

  const handleAdd = () => {
    addItem({
      id: `surface_box_${opt.id}`,
      type: "surface",
      label: `${BOX.name} — ${opt.label}`,
      price,
      quantity: 1,
    });
    toast.success(`${BOX.name} (${opt.label}) ajouté au panier`);
  };

  return (
    <div data-testid="surface-box" className="p-5 rounded-xl border bg-gradient-to-b from-[#161616] to-[#0B0B0B] border-white/10 hover:border-white/25 transition-all flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 bg-white/5 border border-white/10">
          <Icon className="w-5 h-5 text-white/60" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-white text-sm leading-tight">{BOX.name}</div>
          <div className="text-xs text-white/50 mt-0.5">{BOX.desc}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        {BOX.options.map((o) => {
          const active = opt.id === o.id;
          return (
            <button key={o.id} type="button" onClick={() => setOpt(o)} data-testid={`box-opt-${o.id}`}
              className={`py-2.5 rounded-lg border text-sm font-bold transition-all ${
                active ? "bg-[#E50914]/10 border-[#E50914] text-white" : "bg-[#050505] border-white/15 text-white/70 hover:border-white/30"
              }`}>
              {o.label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/10 mb-3">
        <span className="text-[10px] uppercase tracking-wider text-white/40">Prix</span>
        <span className="text-lg font-black text-[#E50914]">{price}€</span>
      </div>

      <button type="button" onClick={handleAdd} data-testid="surface-add-box"
        className="mt-auto w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-xs tracking-wider px-4 py-3 rounded-lg border border-[#E50914] hover:shadow-[0_0_20px_rgba(229,9,20,0.4)] transition-all">
        <ShoppingBag className="w-4 h-4" /> Ajouter au panier
      </button>
    </div>
  );
}

/* --- Places de parking : quantité --- */
function ParkingCard({ addItem }) {
  const [qty, setQty] = useState(1);
  const Icon = PARKING.icon;
  const { pricing } = usePricing();
  const pricePerPlace = pricing.surfaces?.parking ?? PARKING.pricePerPlace;
  const total = qty * pricePerPlace;

  const handleAdd = () => {
    addItem({
      id: "surface_parking",
      type: "surface",
      label: PARKING.name,
      price: pricePerPlace,
      quantity: qty,
    });
    toast.success(`${qty} place${qty > 1 ? "s" : ""} de parking ajoutée${qty > 1 ? "s" : ""} au panier`);
    setQty(1);
  };

  return (
    <div data-testid="surface-parking" className="p-5 rounded-xl border bg-gradient-to-b from-[#161616] to-[#0B0B0B] border-white/10 hover:border-white/25 transition-all flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 bg-white/5 border border-white/10">
          <Icon className="w-5 h-5 text-white/60" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-white text-sm leading-tight">{PARKING.name}</div>
          <div className="text-xs text-white/50 mt-0.5">{PARKING.desc}</div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/10 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40">Prix / place</div>
          <div className="text-lg font-black text-white">{pricePerPlace}€</div>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} data-testid="parking-minus"
            className="w-8 h-8 rounded flex items-center justify-center bg-white/5 border border-white/15 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
            <Minus className="w-4 h-4" />
          </button>
          <div className="w-10 text-center font-black text-lg text-white">{qty}</div>
          <button type="button" onClick={() => setQty((q) => q + 1)} data-testid="parking-plus"
            className="w-8 h-8 rounded flex items-center justify-center bg-[#E50914]/20 border border-[#E50914]/40 hover:bg-[#E50914]/40 transition-all">
            <Plus className="w-4 h-4 text-[#E50914]" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-wider text-white/40">Total</span>
        <span className="text-lg font-black text-[#E50914]">{total}€</span>
      </div>

      <button type="button" onClick={handleAdd} data-testid="surface-add-parking"
        className="mt-auto w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-xs tracking-wider px-4 py-3 rounded-lg border border-[#E50914] hover:shadow-[0_0_20px_rgba(229,9,20,0.4)] transition-all">
        <ShoppingBag className="w-4 h-4" /> Ajouter au panier
      </button>
    </div>
  );
}

/* --- Petites surfaces : au m² --- */
function SurfaceM2Card({ service, addItem }) {
  const [m2, setM2] = useState("");
  const Icon = service.icon || Ruler;
  const { pricing } = usePricing();
  const pricePerM2 = pricing.surfaces?.[service.id] ?? service.pricePerM2;
  const surface = parseFloat(m2) || 0;
  const total = Math.round(surface * pricePerM2);

  const handleAdd = () => {
    if (surface <= 0) {
      toast.error("Indiquez d'abord votre surface en m²");
      return;
    }
    addItem({
      id: `surface_${service.id}_${Date.now()}`,
      type: "surface",
      label: `${service.name} (${surface} m²)`,
      price: total,
      quantity: 1,
      meta: `${surface} m² × ${pricePerM2}€/m²`,
    });
    toast.success(`${service.name} ajouté au panier`);
    setM2("");
  };

  return (
    <div data-testid={`surface-m2-${service.id}`}
      className="group rounded-xl border bg-gradient-to-b from-[#161616] to-[#0B0B0B] border-white/10 hover:border-white/25 transition-all flex flex-col overflow-hidden">
      {service.image && (
        <div className="relative h-28 overflow-hidden">
          <img src={service.image} alt={service.name} loading="lazy" className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.style.display='none';}} />
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 bg-white/5 border border-white/10">
          <Icon className="w-5 h-5 text-white/60" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-white text-sm leading-tight">{service.name}</div>
          <div className="text-xs text-white/50 mt-0.5">{service.desc}</div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/10 mb-3">
        <span className="text-[10px] uppercase tracking-wider text-white/40">Prix au m²</span>
        <span className="text-lg font-black text-white">{pricePerM2}€<span className="text-xs text-white/40">/m²</span></span>
      </div>

      <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Votre surface (m²)</label>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <input type="number" min="0" inputMode="decimal" value={m2} onChange={(e) => setM2(e.target.value)}
            data-testid={`surface-input-${service.id}`} placeholder="ex : 12"
            className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2.5 text-white focus:outline-none focus:border-[#E50914] transition-colors" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">m²</span>
        </div>
        <div className="text-right min-w-[64px]">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Total</div>
          <div className="text-lg font-black text-[#E50914]">{total > 0 ? `${total}€` : "—"}</div>
        </div>
      </div>

      <button type="button" onClick={handleAdd} data-testid={`surface-add-${service.id}`}
        className="mt-auto w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-xs tracking-wider px-4 py-3 rounded-lg border border-[#E50914] hover:shadow-[0_0_20px_rgba(229,9,20,0.4)] transition-all">
        <ShoppingBag className="w-4 h-4" /> Ajouter au panier
      </button>
      </div>
    </div>
  );
}

function Pitch({ icon: Icon, title, desc }) {
  return (
    <div className="p-5 rounded-xl bg-[#0A0A0A] border border-white/10 flex items-center gap-3">
      <div className="w-11 h-11 rounded-lg bg-[#E50914]/10 border border-[#E50914]/30 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#E50914]" />
      </div>
      <div>
        <div className="font-bold text-white text-sm">{title}</div>
        <div className="text-xs text-white/55">{desc}</div>
      </div>
    </div>
  );
}
